import styled, { css } from 'styled-components';

import { mixin } from '@wconcept/wconcept-ui-renewal/styles/index';

interface IProps {
  $perView?: number;
  $offsetLR?: number;
  $colGap?: number;
  $rowGap?: number;
  $marginT?: number;
  $marginB?: number;
  $paddingT?: number;
  $paddingB?: number;
  $line?: number;
  $snap?: boolean;
  $scrollAlign?: 'left' | 'center';
}

export const ItemsScrollBar = styled.div<IProps>`
  position: relative;
  ${mixin.flex({ align: 'flex-start', justify: 'flex-start' })};
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior: contain auto;
  margin: ${({ $marginT }) => $marginT}px 0 ${({ $marginB }) => $marginB}px;
  padding: ${({ $paddingT }) => $paddingT}px 0 ${({ $paddingB }) => $paddingB}px;

  ${props => {
    if (props.$line !== 1) {
      return css`
        flex-direction: column;
        row-gap: ${props.$rowGap}px;

        > .inner-items-scrollBar {
          ${mixin.flex({ align: 'flex-start', justify: 'flex-start' })};
          width: 100%;
        }
      `;
    }
    return null;
  }}

  ${props => {
    if (props.$snap !== true) return null;

    /**
     * center : 아이템 박스가 아니라 그 안의 카드가 화면 가운데 오게 맞춘다.
     * 아이템 박스는 왼쪽에만 padding-left(offsetLR)를 갖고 있어 박스 중심이
     * 카드 중심보다 offsetLR/2 만큼 왼쪽에 있다. snapport 오른쪽을 offsetLR 만큼
     * 줄여(scroll-padding-right) 기준 중심을 그만큼 왼쪽으로 옮겨 상쇄한다.
     *
     * 처음·마지막 아이템은 가운데로 갈 스크롤 범위가 없어 브라우저가 0/최대값으로
     * 잘라내므로, 따로 처리하지 않아도 좌·우 끝에 붙는다.
     */
    const isCenter = props.$scrollAlign === 'center';

    return css`
      scroll-snap-type: x mandatory;
      ${isCenter &&
      css`
        scroll-padding-right: ${props.$offsetLR || 20}px;
      `}

      > .items-grid,
      > .item {
        scroll-snap-align: ${isCenter ? 'center' : 'start'};
        scroll-snap-stop: always;
      }
    `;
  }}

  &:not(.item-auto) > .items-grid,
  &:not(.item-auto) > .item,
  &:not(.item-auto) .inner-items-scrollBar > .item {
    ${props => {
      const margin = props.$offsetLR || 20;
      const colGap = props.$colGap || 6;
      const perView = props.$perView || 2;
      const compVal = (margin - (margin - colGap) * (perView - 1)) / perView;

      return css`
        min-width: calc(${100 / perView}% - ${compVal}px);
        max-width: calc(${100 / perView}% - ${compVal}px);
        padding-left: ${margin}px;
        margin-left: ${colGap - margin}px;

        &:first-child {
          margin-left: 0;
        }

        &:last-child {
          min-width: calc(${100 / perView}% - ${compVal - margin}px);
          padding-right: ${margin}px;

          &.more {
            min-width: 26.67vw;
            height: ${perView === 3 ? 'calc(44.2vw - 20px)' : '48.324vw'};
            padding-left: 0;
            margin-left: 6px;
          }
        }
      `;
    }}
  }

  &.item-auto {
    flex-direction: column;
    column-gap: ${({ $colGap }) => $colGap}px;
    row-gap: ${({ $rowGap }) => $rowGap}px;
    padding-left: ${({ $offsetLR }) => $offsetLR}px;
    padding-right: ${({ $offsetLR }) => $offsetLR}px;

    .inner-items-scrollBar {
      width: max-content;
      ${mixin.flex({
        align: 'flex-start',
        justify: 'flex-start',
      })};
      row-gap: inherit;
      column-gap: inherit;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${({ theme }) => theme.media.smMax} {
    margin-top: ${({ $marginT = 0 }) => `${($marginT / 375) * 100}vw`};
    margin-bottom: ${({ $marginB = 0 }) => `${($marginB / 375) * 100}vw`};
    padding-top: ${({ $paddingT = 0 }) => `${($paddingT / 375) * 100}vw`};
    padding-bottom: ${({ $paddingB = 0 }) => `${($paddingB / 375) * 100}vw`};

    ${props =>
      props.$snap === true && props.$scrollAlign === 'center'
        ? css`
            scroll-padding-right: ${((props.$offsetLR || 20) / 375) * 100}vw;
          `
        : null}

    ${props => {
      if (props.$line !== 1) {
        return css`
          row-gap: ${mixin.pxToVw(String(props.$rowGap))};
        `;
      }
      return null;
    }}

    &:not(.item-auto) > .items-grid,
    &:not(.item-auto) > .item,
    &:not(.item-auto) .inner-items-scrollBar > .item {
      ${props => {
        const margin = ((props.$offsetLR || 20) / 375) * 100;
        const colGap = ((props.$colGap || 6) / 375) * 100;
        const perView = props.$perView || 2;
        const compVal = (margin - (margin - colGap) * (perView - 1)) / perView;

        return css`
          min-width: calc(${100 / perView}% - ${compVal}vw);
          max-width: calc(${100 / perView}% - ${compVal}vw);
          padding-left: ${margin}vw;
          margin-left: ${colGap - margin}vw;

          &:first-child {
            margin-left: 0;
          }

          &:last-child {
            min-width: calc(${100 / perView}% - ${compVal - margin}vw);
            padding-right: ${margin}vw;

            &.more {
              min-width: 22.935vw;
              height: ${perView === 3
                ? 'calc(44.2vw - 5.333333333333333vw)'
                : '48.324vw'};
              padding-left: 0;
              margin-left: ${mixin.pxToVw('6')};
            }
          }
        `;
      }}
    }

    &.item-auto {
      column-gap: ${({ $colGap }) => mixin.pxToVw(String($colGap))};
      row-gap: ${({ $rowGap }) => mixin.pxToVw(String($rowGap))};
      padding-left: ${({ $offsetLR }) => mixin.pxToVw(String($offsetLR))};
      padding-right: ${({ $offsetLR }) => mixin.pxToVw(String($offsetLR))};
    }
  }

  @media ${({ theme }) => theme.media.pc} {
    &.item-auto {
      .inner-items-scrollBar {
        width: 100%;
        flex-wrap: wrap;
      }
    }
  }
`;
